import React, { useState, useEffect } from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import { PortableText } from '@portabletext/react';
import {
  chakra,
  Box,
  UnorderedList,
  ListItem,
  Text,
  Flex,
  FormControl,
  FormLabel,
  Container,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Button,
  Select,
  Grid,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  VisuallyHidden,
  Link,
} from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { geoMiller } from 'd3-geo-projection';
import { ComposableMap, Geographies, Geography, Marker } from 'react-simple-maps';
import { roundNumber } from '../../helpers/numbers';
import { alphabetizeByProperty } from '../../helpers/arrays';
import { slugify } from '../../helpers/strings';
import { COUNTRIES } from './constants/countries';

// RSM Geographies
import geography from './geographies/world-110m-shapes.json';
import lakes from './geographies/world-110m-lakes.json';
// Notes:
// - File: '../data/world-110m-shapes.json'
// - Source: https://international.ipums.org/international/gis.shtml
// - Converted to TopoJSON with https://mapshaper.org/
//     Simplification settings: 0.40%, use planar geometry, Visvalingam/weighted area method
// - Merged with data of '../data/raw/world-110m-raw.json' from RSM, extraneous data removed
// - Data modified to match IMF data/taxonomies

//
// Local Variables
//

// RSM Map Projection
const customProjectionWidth = 700;
const customProjectionHeight = 450;
const customProjection = geoMiller()
  .translate([customProjectionWidth / 2, customProjectionHeight / 2])
  .rotate([-10, 0, 0]);

// RSM Chakra Components
const ChakraGeography = chakra(Geography);
const ChakraMarker = chakra(Marker);
const ChakraMarkerGroup = chakra('g');
const ChakraMarkerCircle = chakra('circle');
const ChakraMarkerPath = chakra('path');

// Other
const hoverIntentDelay = 250; // ms

//
// Local Utilities
//

const getAgreementTotal = (countries) => {
  if (!countries) return null;
  const total = countries.reduce((acc, { amount }) => acc + amount, 0);
  return roundNumber(total, 'millions', 'all');
};

const handleMarkerHover = (marker, restoreToIndex = false) => {
  // SVG elements don't support z-index, so we need to manipulate the DOM instead
  const parent = marker.parentNode;

  // Restorethe market's original location in the parent node if passed an index
  if (restoreToIndex) {
    parent.insertBefore(marker, parent.children[restoreToIndex]);
    return;
  }

  // Otherwise move the hovered marker to the end of the parent node
  parent.append(marker);
};

//
// Component
//

function LendingMap({ data }) {
  const mapData = useStaticQuery(MAP_QUERY);
  const { title, description, exchangeRates, footerMeta } = data;
  const { agreements } = mapData.data;
  const [activeModal, setActiveModal] = useState(null);
  const [activeAgreement, setActiveAgreement] = useState(''); // Can't use `null` to update Select state to default value
  const [activeCountry, setActiveCountry] = useState(''); // Can't use `null` to update Select state to default value
  const [hoveredAgreement, setHoveredAgreement] = useState(null);
  const [hoveredCountry, setHoveredCountry] = useState(null);

  useEffect(() => {
    if (activeModal !== null) {
      window.location.hash = 'agreement-details';
    } else {
      window.location.hash = '';
    }

    // Listen for hash changes and update the modal state accordingly
    const handleHashChange = () => {
      if (window.location.hash === '') {
        setActiveModal(null);
      }
    };
    window.addEventListener('hashchange', handleHashChange);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [activeModal]);

  // Countries With Agreements
  // Go through all the agreements and create a new array of countries, including the agreements they are a part of
  const countriesWithAgreements = agreements.reduce((acc, { countries, type, abbreviation }) => {
    if (countries === null) return acc;

    countries.forEach((country) => {
      const { _key, name, amount } = country;
      const countryIndex = acc.findIndex((country) => country.name === name);

      if (countryIndex === -1) {
        acc.push({
          _key,
          name,
          countryAgreements: [{ type, amount, abbreviation }],
        });
      } else {
        acc[countryIndex].countryAgreements.push({ type, amount, abbreviation });
      }
    });
    return alphabetizeByProperty(acc, 'name');
  }, []);

  return (
    <Grid gap={10} px={{ base: 30, xl: 60 }}>
      <Box as='header'>
        <Text as='h2' textStyle='h2'>
          {title}
        </Text>
        <PortableText value={description} />
      </Box>
      <Flex
        flexDirection='column'
        gap={20}
        padding={{ base: 20, lg: 50 }}
        background='grey.light'
        overflow='hidden'
      >
        <Flex>
          <CountriesList
            agreements={agreements}
            countriesWithAgreements={countriesWithAgreements}
            activeAgreement={activeAgreement}
            setActiveAgreement={setActiveAgreement}
            activeCountry={activeCountry}
            setActiveCountry={setActiveCountry}
            setHoveredCountry={setHoveredCountry}
          />
          <Flex flex='1' ml='-140' mr='-20' display={{ base: 'none', lg: 'flex' }}>
            <ComposableMap
              id='lending-map'
              className='lending-map'
              projection={customProjection}
              width={customProjectionWidth}
              height={customProjectionHeight - 100} // Relative height cuts off the bottom of the map (hides Antarctica)
            >
              {/* Geographies */}
              <Geographies className='lending-map__geography' geography={geography}>
                {({ geographies }) =>
                  geographies
                    .filter((geo) => geo.properties.CONTINENT !== 'Antarctica') // Remove Antarctica
                    .filter((geo) => geo.properties.ISO_A2) // Remove countries without ISO_A2 codes
                    .map((geo) => (
                      <ChakraGeography
                        key={`geography-${geo.rsmKey}`}
                        id={`geography-${slugify(geo.properties.ISO_A2)}`}
                        geography={geo}
                        fill='grey.medium'
                        stroke='grey.medium'
                        strokeWidth='0.1px' // Fill half-pixel gaps so land borders are never visible
                        tabIndex='-1'
                        pointerEvents='none'
                      />
                    ))
                }
              </Geographies>

              {/* Lakes */}
              <Geographies className='lending-map__lakes' geography={lakes}>
                {({ geographies }) =>
                  geographies.map((geo) => (
                    <ChakraGeography
                      key={`lake-${geo.rsmKey}`}
                      geography={geo}
                      fill='grey.light'
                      tabIndex='-1'
                      pointerEvents='none'
                    />
                  ))
                }
              </Geographies>

              {/* All Markers - FOR TESTING */}
              {/* {COUNTRIES.map(({ name, capitalCoordinates, markerOffset = 8 }) => (
                <Marker key={`test-${slugify(name)}`} coordinates={capitalCoordinates}>
                  <circle r={3} fill='orange' stroke='black' strokeWidth='0.5px' />
                  <text textAnchor='middle' y={markerOffset} style={{ fontSize: '6px' }}>{name}</text>
                </Marker>
              ))} */}

              {/* Markers */}
              <g className='lending-map__markers'>
                {countriesWithAgreements.map(({ name, countryAgreements }, index) => {
                  const { capitalCoordinates } = COUNTRIES.find((element) => element.name === name);

                  // Ignore markers without coordinates
                  if (!capitalCoordinates) return null;

                  const isActiveAgreement = countryAgreements.find(
                    (element) => slugify(element.abbreviation) === activeAgreement,
                  );
                  const isActiveCountry = slugify(name) === activeCountry;
                  const isHoveredAgreement = countryAgreements.find(
                    (element) => slugify(element.abbreviation) === hoveredAgreement,
                  );
                  const isHoveredCountry = slugify(name) === hoveredCountry;
                  const isActive =
                    (!activeAgreement || isActiveAgreement) &&
                    (!activeCountry || isActiveCountry) &&
                    (!hoveredAgreement || isHoveredAgreement) &&
                    (!hoveredCountry || isHoveredCountry);

                  return (
                    <Tooltip
                      key={`tooltip-${slugify(name)}-${index}`}
                      hasArrow
                      gutter={16}
                      placement={
                        capitalCoordinates[0] > 70 || capitalCoordinates[0] < -170
                          ? 'left'
                          : 'right'
                      } // `< -170` is for Tonga, which has a negative longitude
                      closeOnClick={false}
                      p={10}
                      borderRadius='md'
                      width={200}
                      backdropFilter='blur(10px)'
                      label={
                        <Box>
                          <Text textStyle='h6' textTransform='uppercase'>
                            {name}
                          </Text>
                          <UnorderedList spacing={5} styleType='none' margin='0.3125rem 0 0 0'>
                            {countryAgreements.map(({ amount, abbreviation }, index) => (
                              <ListItem
                                key={`tooltip-agreement-${slugify(abbreviation)}-${index}`}
                                display='flex'
                                width='100%'
                                justifyContent='space-between'
                                alignItems='baseline'
                              >
                                <Box
                                  width='10px'
                                  height='10px'
                                  borderRadius='50%'
                                  bg={`agreement.${slugify(abbreviation)}`}
                                  marginRight={5}
                                  outline='1.5px solid white'
                                />
                                <Text fontSize='xs'>{abbreviation}</Text>
                                <Box
                                  flex='1'
                                  borderBottom='1px dotted white'
                                  opacity='0.5'
                                  mx={5}
                                />
                                <Text fontSize='xs' fontWeight='bold'>
                                  {roundNumber(amount, 'millions', 'all')}
                                </Text>
                                <Text fontSize='xs' mx={5}>
                                  M<VisuallyHidden>illion</VisuallyHidden> SDR
                                </Text>
                              </ListItem>
                            ))}
                          </UnorderedList>
                        </Box>
                      }
                    >
                      <ChakraMarker
                        key={`marker-${slugify(name)}-${index}`}
                        className={
                          isHoveredAgreement || isHoveredCountry ? 'rsm-marker--hover' : null
                        }
                        coordinates={capitalCoordinates}
                        opacity={isActive ? 1 : 0}
                        pointerEvents={!isActive && 'none'}
                        transition='opacity 0.1s ease'
                        _hover={{
                          '> g': {
                            transform: 'scale(1.2)',
                          },
                        }}
                        sx={{
                          '&.rsm-marker--hover > g': {
                            transform: 'scale(1.2)',
                          },
                        }}
                        onMouseEnter={(event) =>
                          handleMarkerHover(event.target.closest('.rsm-marker'))
                        }
                        onMouseLeave={(event) =>
                          handleMarkerHover(event.target.closest('.rsm-marker'), index)
                        }
                      >
                        {countryAgreements.map(({ abbreviation }, index) => (
                          <ChakraMarkerGroup
                            key={`marker-${slugify(name)}-${index}`}
                            transition='transform 0.1s ease'
                            filter='drop-shadow(0 1px 3px rgba(0, 0, 0, 0.16))' // Similar to `var(--chakra-shadows-base`
                          >
                            {/* 1st agreement base circle */}
                            {index === 0 && (
                              <ChakraMarkerCircle
                                r={6}
                                fill={`agreement.${slugify(abbreviation)}`}
                              />
                            )}
                            {/* 2nd agreement half circle */}
                            {index === 1 && (
                              <ChakraMarkerPath
                                fill={`agreement.${slugify(abbreviation)}`}
                                transform='translate(-6px, -6px)'
                                d='M6 12A6 6 0 1 0 6 0v12Z'
                              />
                            )}
                            {/* 3rd agreement bottom slice */}
                            {index === 2 && (
                              <ChakraMarkerPath
                                fill={`agreement.${slugify(abbreviation)}`}
                                transform='translate(-6px, -6px)'
                                d='M.803 9.001 6.001 6l5.196 3A5.997 5.997 0 0 1 6 12 5.997 5.997 0 0 1 .803 9.001Z'
                              />
                            )}
                            {/* Add stroke at end of loop */}
                            {index === countryAgreements.length - 1 && (
                              <ChakraMarkerCircle
                                r={6}
                                fill='none'
                                stroke='white'
                                strokeWidth='1px'
                              />
                            )}
                          </ChakraMarkerGroup>
                        ))}
                      </ChakraMarker>
                    </Tooltip>
                  );
                })}
              </g>
            </ComposableMap>
          </Flex>
        </Flex>
        <AgreementButtons
          agreements={agreements}
          setHoveredAgreement={setHoveredAgreement}
          setActiveModal={setActiveModal}
        />
      </Flex>
      <Flex
        flexDirection={{ base: 'column', md: 'row' }}
        gap={5}
        justifyContent={{ md: 'space-between' }}
      >
        <Box fontSize='xs'>
          <PortableText value={exchangeRates} />
        </Box>
        <Box fontSize='xs' textAlign={{ md: 'right' }}>
          <PortableText value={footerMeta} />
        </Box>
      </Flex>
      <AgreementModals
        agreements={agreements}
        activeModal={activeModal}
        setActiveModal={setActiveModal}
      />
    </Grid>
  );
}

//
// Subcomponents
//

const CountriesList = ({
  agreements,
  countriesWithAgreements,
  activeAgreement,
  setActiveAgreement,
  activeCountry,
  setActiveCountry,
  setHoveredCountry,
}) => {
  let countryHoverIntent;

  const handleCountryHoverIntent = () => {
    countryHoverIntent = setTimeout(() => {
      setHoveredCountry(null);
    }, hoverIntentDelay);
  };

  return (
    <Flex
      className='lending-map__countries-list'
      flexDirection='column'
      background='white'
      position='relative'
      height='560px'
      width={{ base: '100%' }}
      minWidth={{ lg: 280 }}
      maxWidth={{ lg: 280 }}
      boxShadow='md'
      borderRadius='md'
      overflow='hidden'
      _before={{
        content: '""',
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: '100%',
        height: '50px',
        background: 'linear-gradient(transparent 0%, white 90%)',
        pointerEvents: 'none',
      }}
    >
      <Flex
        flexDirection={{ base: 'column', md: 'row', lg: 'column' }}
        gap={10}
        padding={20}
        borderBottom='1px solid lightGrey'
      >
        <FormControl position='relative'>
          <FormLabel fontSize='xs' color='grey' mb={5}>
            Agreement Type
          </FormLabel>
          <Select
            value={activeAgreement}
            placeholder='All Agreement Types'
            aria-label='Filter agreements by type'
            aria-controls='lending-map'
            fontWeight={!activeAgreement && '300'}
            onChange={(event) => {
              setActiveAgreement(event.target.value);
            }}
          >
            {agreements.map(({ type, abbreviation }, index) => (
              <option key={`agreement-type-select-${index}`} value={slugify(abbreviation)}>
                {type}
              </option>
            ))}
          </Select>
          <Button
            variant='link'
            display={!activeAgreement && 'none'}
            position='absolute'
            top={0}
            right={0}
            onClick={() => {
              setActiveAgreement('');
            }}
            aria-controls='lending-map'
            aria-label='Clear agreement type filter'
          >
            <Text fontSize='xs' fontWeight='300'>
              Clear
            </Text>
          </Button>
        </FormControl>
        <FormControl position='relative'>
          <FormLabel fontSize='xs' color='grey' mb={5}>
            Country
          </FormLabel>
          <Select
            value={activeCountry}
            placeholder='All Countries'
            aria-label='Filter agreements by country'
            aria-controls='lending-map'
            fontWeight={!activeCountry && '300'}
            onChange={(event) => {
              setActiveCountry(event.target.value);
            }}
          >
            {countriesWithAgreements.map(({ name }, index) => (
              <option key={`country-select-${index}`} value={slugify(name)}>
                {name}
              </option>
            ))}
          </Select>
          <Button
            variant='link'
            display={!activeCountry && 'none'}
            position='absolute'
            top={0}
            right={0}
            onClick={() => {
              setActiveCountry('');
            }}
            aria-controls='lending-map'
            aria-label='Clear country filter'
          >
            <Text fontSize='xs' fontWeight='300'>
              Clear
            </Text>
          </Button>
        </FormControl>
      </Flex>
      <UnorderedList
        styleType='none'
        flex='1'
        height='560px'
        overflowY='scroll'
        padding='10px 10px 20px'
        ml='0'
        sx={{
          '&:empty': {
            display: 'none', // Fancy CSS for hiding the list when it's empty
          },
        }}
        onMouseLeave={() => {
          clearTimeout(countryHoverIntent);
          setHoveredCountry(null);
        }}
      >
        {countriesWithAgreements.map(({ _key, name, countryAgreements }) => {
          const isActiveAgreement = countryAgreements.find(
            (element) => slugify(element.abbreviation) === activeAgreement,
          );
          const isActiveCountry = slugify(name) === activeCountry;
          const isActive =
            (!activeAgreement || isActiveAgreement) && (!activeCountry || isActiveCountry);

          // Don't return a country ListItem if it's not active
          if (!isActive) return null;

          return (
            <ListItem
              key={`countries-list-${_key}`}
              marginY={{ base: 5, sm: 10 }}
              padding={10}
              borderRadius='sm'
              transition='background 0.1s ease'
              _hover={{
                background: 'grey.light',
              }}
              onMouseEnter={() => {
                clearTimeout(countryHoverIntent);
                setHoveredCountry(slugify(name));
              }}
              onMouseLeave={() => {
                handleCountryHoverIntent();
              }}
            >
              <Text textStyle='h6' textTransform='uppercase'>
                {name}
              </Text>
              <UnorderedList spacing={5} styleType='none' margin='0.3125rem 0 0 0'>
                {countryAgreements.map(({ amount, abbreviation }, index) => {
                  // Don't return a country's agreement ListItem if it's not active
                  if (activeAgreement && slugify(abbreviation) !== activeAgreement) return null;

                  return (
                    <ListItem
                      key={`countries-list-${slugify(name)}-${slugify(abbreviation)}-${index}`}
                      display='flex'
                      width='100%'
                      justifyContent='space-between'
                      alignItems='baseline'
                    >
                      <Box
                        width='10px'
                        height='10px'
                        borderRadius='50%'
                        bg={`agreement.${slugify(abbreviation)}`}
                        marginRight={5}
                      />
                      <Text>{abbreviation}</Text>
                      <Box flex='1' borderBottom='1px dotted lightGrey' mx={5} />
                      <Text textStyle='h6'>{roundNumber(amount, 'millions', 'all')}</Text>
                      <Text fontSize='xs' mx={5}>
                        M<VisuallyHidden>illion</VisuallyHidden> SDR
                      </Text>
                    </ListItem>
                  );
                })}
              </UnorderedList>
            </ListItem>
          );
        })}
      </UnorderedList>
      <Flex
        flexDirection='column'
        gap={10}
        alignItems='center'
        paddingTop='20px'
        sx={{
          '.lending-map__countries-list > ul:not(:empty) + &': {
            display: 'none', // Fancy CSS for showing the empty list message
          },
        }}
      >
        <Text>No matching results.</Text>
        <Button
          variant='secondary'
          onClick={() => {
            setActiveAgreement('');
            setActiveCountry('');
          }}
          aria-controls='lending-map'
        >
          Clear All Filters
        </Button>
      </Flex>
    </Flex>
  );
};

const AgreementButtons = ({ agreements, setHoveredAgreement, setActiveModal }) => {
  let agreementHoverIntent;

  const handleAgreementHoverIntent = () => {
    agreementHoverIntent = setTimeout(() => {
      setHoveredAgreement(null);
    }, hoverIntentDelay);
  };

  return (
    <Flex gap={10} flexDirection='column'>
      <Text fontSize='xs' fontWeight='300' textAlign='center'>
        Click on a Lending Agreement to see more info
      </Text>
      <Flex
        gap={{ base: '10', md: '20' }}
        justifyContent='center'
        flexWrap='wrap'
        overflow='visible'
        onMouseLeave={() => {
          clearTimeout(agreementHoverIntent);
          setHoveredAgreement(null);
        }}
      >
        {agreements.map(({ abbreviation, type, countries }, index) => (
          <Button
            key={`agreement-button-${slugify(abbreviation)}-${index}`}
            position='relative'
            flex='0' // Don't let buttons grow to fill the space
            flexBasis={{
              base: '100%',
              sm: 'calc(50% - (10px / 2))',
              md: 'calc(25% - (20px * 3 / 4))',
              lg: 'calc(20% - (20px * 4 / 5))',
            }} // Account for gaps from parent wrapper relative to items in the row
            display='flex'
            flexDirection={{ base: 'row-reverse', sm: 'column' }}
            justifyContent={{ base: 'space-between', sm: 'flex-start' }}
            alignItems='center'
            textAlign={{ base: 'left', sm: 'center' }}
            gap={{ base: 10, sm: 5 }}
            height='auto'
            padding={{ base: '10', sm: '15', md: '20' }}
            borderWidth={{ base: '0 0 0 5px', sm: '0 0 5px' }}
            borderStyle='solid'
            borderColor={`agreement.${slugify(abbreviation)}`}
            background='white'
            borderRadius='md'
            boxShadow='md'
            _hover={{
              '--beforeOpacity': '1', // Set up hover effect that can't be achieved with boxShadow (Chakra can't handle theme colors in boxShadow... bug?)
              background: 'white',
            }}
            _before={{
              content: '""',
              position: 'absolute',
              display: 'block',
              top: '0',
              bottom: { base: '0', sm: '-5px' }, // Offset main element borderBottom width when it's there
              left: { base: '-5px', sm: '0' }, // Offset main element borderLeft width when it's there
              right: '0',
              border: '2px solid',
              borderRadius: 'md',
              borderColor: `agreement.${slugify(abbreviation)}`,
              opacity: 'var(--beforeOpacity, 0)',
              transition: 'inherit',
            }}
            onClick={() => {
              setActiveModal(index);
            }}
            onMouseEnter={() => {
              clearTimeout(agreementHoverIntent);
              setHoveredAgreement(slugify(abbreviation));
            }}
            onMouseLeave={() => {
              handleAgreementHoverIntent();
            }}
          >
            <Flex gap={5} alignItems='baseline'>
              {/* Text below needs 'h3' size override for mobile */}
              <Text textStyle='h3' fontSize={{ base: 'sm', sm: '1.375rem' }}>
                {getAgreementTotal(countries)}
              </Text>
              <Text fontSize='xs' fontWeight='400'>
                M<VisuallyHidden>illion</VisuallyHidden> SDR
              </Text>
            </Flex>
            <Text fontSize='sm' whiteSpace='normal' maxWidth={{ base: '60%', sm: 'none' }}>
              {type} ({abbreviation})
            </Text>
          </Button>
        ))}
      </Flex>
    </Flex>
  );
};

const AgreementModals = ({ agreements, activeModal, setActiveModal }) => {
  const hasTieredInterestRateGroup = (countries) =>
    countries.some(({ tieredInterestRateGroup }) => tieredInterestRateGroup);

  return agreements.map(({ abbreviation, type, adopted, countries, details, footnotes }, index) => {
    alphabetizeByProperty(countries, 'name');

    return (
      <Modal
        key={`agreement-modal-${slugify(abbreviation)}-${index}`}
        isOpen={activeModal === index}
        onClose={() => setActiveModal(null)}
        size='full'
      >
        <ModalOverlay />
        <ModalContent>
          <ModalBody background='white' padding='0'>
            <Container maxWidth='container.xl' px={{ base: 20, xl: 60 }}>
              <Flex flexDirection='column' gap={10} as='section'>
                <Flex
                  as='header'
                  alignItems={{ base: 'flex-end', xl: 'center' }}
                  justifyContent='space-between'
                  width='100%'
                  gap={10}
                  py={20}
                  mb={20}
                  borderBottom='1px solid lightGrey'
                  bg='white'
                  position='sticky'
                  top='0'
                >
                  <Flex flexDirection='column' flex='1' gap={5}>
                    <Text as='h2' textStyle='h2'>
                      {type}
                    </Text>
                    <Flex alignItems='center'>
                      <Box
                        height='15px'
                        flex='15px 0 0'
                        borderRadius='50%'
                        bg={`agreement.${slugify(abbreviation)}`}
                        marginRight={5}
                      />
                      <Flex justifyContent='center' alignItems='center'>
                        <Text>{abbreviation}</Text>
                        {adopted && (
                          <Text
                            _before={{
                              content: '""',
                              display: 'inline-block',
                              width: '3px',
                              height: '3px',
                              borderRadius: '50%',
                              background: 'black',
                              margin: '4px 5px',
                            }}
                          >
                            Adopted {adopted}
                          </Text>
                        )}
                      </Flex>
                    </Flex>
                    <Flex flexDirection='row' gap={5} alignItems='flex-end'>
                      <Text textStyle='h3'>{getAgreementTotal(countries)}</Text>
                      <Text fontSize='sm'>Million SDR</Text>
                    </Flex>
                  </Flex>

                  <ModalCloseButton
                    background='navy'
                    color='white'
                    padding={20}
                    borderRadius='100%'
                    position='relative'
                    alignSelf='flex-start'
                    marginLeft={20}
                    transition='background 0.2s ease-in-out'
                    _hover={{
                      background: 'black',
                    }}
                  />
                </Flex>

                <Grid
                  templateColumns={{ base: '1fr', xl: 'repeat(2, 1fr)' }}
                  gap={{ base: 10, xl: 30 }}
                >
                  {details &&
                    details.map(({ _key, title, content }) => (
                      <Box key={`modal-details-${_key}`} marginBottom={10}>
                        <Text textStyle='h5'>{title}</Text>
                        <PortableText value={content} />
                      </Box>
                    ))}
                </Grid>

                <Flex as='section' flexDirection='column' mt={20} gap={10}>
                  <Text textStyle='h5'>Distribution of Funds</Text>
                  <TableContainer border='1px solid lightGrey'>
                    <Table variant='striped' colorScheme='gray'>
                      <Thead>
                        <Tr>
                          <Th>Country</Th>
                          <Th>Date of Approval</Th>
                          <Th>Amount (SDR)</Th>
                          {hasTieredInterestRateGroup(countries) && (
                            <Th maxWidth='100px'>
                              Tiered Interest
                              <br /> Rate Group¹
                            </Th>
                          )}
                          <Th>Press Release</Th>
                        </Tr>
                      </Thead>
                      <Tbody>
                        {countries?.length &&
                          countries.map(
                            ({
                              _key,
                              name,
                              approvalDate,
                              amount,
                              link,
                              tieredInterestRateGroup,
                            }) => (
                              <Tr key={`modal-agreement-${_key}`}>
                                <Td>{name}</Td>
                                <Td>
                                  <time dateTime={approvalDate}>
                                    {new Date(approvalDate).toLocaleDateString()}
                                  </time>
                                </Td>
                                <Td>
                                  <data value={Math.round(amount)}>
                                    {`${roundNumber(amount, 'millions', 'all')} million`}
                                  </data>
                                </Td>
                                {hasTieredInterestRateGroup(countries) && (
                                  <Td>{tieredInterestRateGroup}</Td>
                                )}
                                <Td>
                                  <Link
                                    variant='primary'
                                    href={link}
                                    aria-label={`View press release for ${name}`}
                                  >
                                    View <ExternalLinkIcon />
                                  </Link>
                                </Td>
                              </Tr>
                            ),
                          )}
                      </Tbody>
                    </Table>
                  </TableContainer>

                  <Flex flexDirection='column' gap={5} marginTop={20} fontSize='xs'>
                    <PortableText value={footnotes} />
                  </Flex>
                </Flex>
              </Flex>
            </Container>
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  });
};

const MAP_QUERY = graphql`
  query MAPQUERY {
    data: allSanityLendingAgreement {
      agreements: nodes {
        adopted
        abbreviation
        type
        countries: _rawCountries
        details: _rawDetails
        footnotes: _rawFootnotes
        _key
      }
    }
  }
`;

export default LendingMap;
